import * as React from 'react';
import { PinnacleTabs } from '../../../../components/PinnacleTabs/PinnacleTabs';
import UserInformationTab from './tabs/UserInformation';
import { PartialEditUserFormRequest, User } from '../../../../api/interfaces/User';
import { Program } from '../../../../api/interfaces/Program';
import { Box } from '@mui/material';
import { School } from '../../../../api/interfaces/School';
import { updateUser } from '../../../../api/services/user';
import { pushNotification } from '../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../../../utils/errorHandler';
import AdditionalSettings from './tabs/AdditionalSettings';
import AddRemoveSubjects from './tabs/AddRemoveSubjects';

interface EditUserDrawerContentProps {
  programs: Program[];
  schools: School[];
  selectedUserId: string;
  selectedUserInfo: User | null;
  onClose: any;
  onSuccess: (updatedUser: User, skipClose?: boolean) => void;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting: boolean;
}

export interface EditUserDrawerContentHandle {
  handleSubmit: () => void;
}

const EditUserDrawerContent = React.forwardRef<EditUserDrawerContentHandle, EditUserDrawerContentProps>(
  ({ programs, schools, selectedUserId, selectedUserInfo, onClose, onSuccess, setIsSubmitting, isSubmitting }, ref) => {

    React.useImperativeHandle(ref, () => ({
      handleSubmit: async () => {
        setIsSubmitting(true);

        const modifiedData = getChangedFields();
        try {
          const updateResponse = await updateUser(selectedUserId, modifiedData);

          /**
           * Send to the parent component (UsersPage) the recent change
           * Close drawer
           * Show toaster
           */
          onSuccess(updateResponse.data as User);
          onClose();
          pushNotification({
            details: updateResponse.message,
            type: NotificationTypes.Success,
          });
        } catch (error) {
          handleApiError(error, {
            notify: (msg) => {
              pushNotification({
                details: msg,
                type: NotificationTypes.Error,
              });
            },
          });
        } finally {
          setIsSubmitting(false);
        }
      }
    }));

    // Centralized form state
    const [formData, setFormData] = React.useState<PartialEditUserFormRequest>({
      email: selectedUserInfo?.email ?? "",
      firstName: selectedUserInfo?.firstName ?? "",
      lastName: selectedUserInfo?.lastName ?? "",
      school: selectedUserInfo?.school ?? "",
      termId: selectedUserInfo?.termId ?? "",
      programId: selectedUserInfo?.programId ?? "",
      isActive: selectedUserInfo?.isActive ?? false,
      expiresAt: selectedUserInfo?.expiresAt ?? "",
      isTrial: selectedUserInfo?.isTrial ?? false,
      undergradSetDate: selectedUserInfo?.undergradSetDate ?? "",
    });

    // Keep a copy of the original values for comparison
    const initialDataRef = React.useRef(formData);

    const handleChange = (field: string, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const getChangedFields = () => {
      const changed: Record<string, unknown> = {};
      for (const key in formData) {
        const typedKey = key as keyof PartialEditUserFormRequest;
        if (formData[typedKey] !== initialDataRef.current[typedKey]) {
          changed[typedKey] = formData[typedKey] as PartialEditUserFormRequest[typeof typedKey];
        }
      }
      return changed;
    };

    return (
      <Box display="flex" flexDirection="column" height="100%">
        <PinnacleTabs
          disabled={isSubmitting}
          tabs={[
            {
              label: "User Information",
              content: (
                <UserInformationTab
                  programs={programs}
                  schools={schools}
                  formData={formData}
                  onChange={handleChange}
                  selectedUserInfo={selectedUserInfo ?? undefined}
                  onSuccess={onSuccess}
                  disabled={isSubmitting}
                  setIsSubmitting={setIsSubmitting}
                />
              ),
              disabledTabItem: false
            },
            {
              label: "Add or Remove Subjects",
              content: (
                <AddRemoveSubjects
                  formData={formData}
                  onChange={handleChange}
                  selectedUserInfo={selectedUserInfo}
                  disabled={isSubmitting}
                  setIsSubmitting={setIsSubmitting}
                />
              ),
              disabledTabItem: (!selectedUserInfo?.programId) ? true : false
            },
            {
              label: "Additional Settings",
              content: (
                <AdditionalSettings
                  formData={formData}
                  onChange={handleChange}
                  email={selectedUserInfo?.email}
                  isTrial={selectedUserInfo?.isTrial}
                  isActive={selectedUserInfo?.isActive}
                  disabled={isSubmitting}
                />
              ),
              disabledTabItem: false
            },
          ]}
        />
      </Box>
    );
  }
);


export default EditUserDrawerContent;
